'use client';

import type { Listing } from '@repo/api';
import { IconChevronRight } from '@repo/svgs';
import { BreadcrumbLink } from '@/components/ui/BreadcrumbLink';
import { cn } from '@/lib/cn';

interface BreadcrumbsProps {
  listing?: Listing | null;
  city?: string;
}

export function Breadcrumbs({ listing = null }: BreadcrumbsProps) {
  const neighborhood = listing?.neighborhood || null;
  const borough = neighborhood?.borough || '';
  const boroughSlug = borough.toLowerCase().replace(/\s+/g, '-');
  const parentSlug = neighborhood?.parentSlug;
  const neighborhoodName = neighborhood?.name || '';
  const neighborhoodSlug = neighborhood?.slug || '';
  const address = listing?.fullAddress?.split(',')[0] || null;

  return (
    <div
      className={cn(
        'flex gap-1 items-center min-w-0 flex-1',
        // Mobile: one line, scrolled horizontally. `[&>*]:shrink-0` keeps the
        // crumbs at full width so they overflow instead of compressing.
        'flex-nowrap overflow-x-auto overscroll-x-contain no-scrollbar [&>*]:shrink-0',
        // `-mx-6` cancels the header's `px-6` so the strip spans the full
        // viewport and overflow is clipped at the screen edge rather than
        // inset from it — a crumb cut off at the edge is the cue that there
        // is more. `px-6` puts the first and last crumb back in line with
        // the page gutter at either end of the scroll.
        '-mx-6 px-6',
        // Desktop has room for the trail, so let it wrap as before.
        'md:flex-wrap md:overflow-hidden md:mx-0 md:px-0',
      )}
    >
      <BreadcrumbLink href="/listings" className="text-caption text-accent py-1">
        NYC listings
      </BreadcrumbLink>

      {borough && (
        <>
          <IconChevronRight className="w-2 h-2 text-secondary shrink-0" />
          <BreadcrumbLink
            href={`/listings/borough:${boroughSlug}`}
            className="text-caption text-accent py-1"
          >
            {borough}
          </BreadcrumbLink>
        </>
      )}

      {parentSlug && (
        <>
          <IconChevronRight className="w-2 h-2 text-secondary shrink-0" />
          <BreadcrumbLink
            href={`/listings/borough:${boroughSlug}|nbr:${parentSlug}`}
            className="text-caption text-accent py-1"
          >
            {parentSlug
              .split('-')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')}
          </BreadcrumbLink>
        </>
      )}

      {neighborhoodName && neighborhoodSlug && (
        <>
          <IconChevronRight className="w-2 h-2 text-secondary shrink-0" />
          <BreadcrumbLink
            href={`/listings/borough:${boroughSlug}|nbr:${neighborhoodSlug}`}
            className="text-caption text-accent py-1"
          >
            {neighborhoodName}
          </BreadcrumbLink>
        </>
      )}

      {address && (
        <>
          <IconChevronRight className="w-2 h-2 text-secondary shrink-0" />
          <BreadcrumbLink active className="text-caption text-secondary py-1">
            {address}
          </BreadcrumbLink>
        </>
      )}
    </div>
  );
}
